// src/pages/ProjectView.tsx
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Upload, FileText, ChevronRight, X, Edit2 } from 'lucide-react';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore'; 
import {
  getProject,
  getLogEntries,
  createLogEntry, 
  getCurrentUser,
  Project,
  BuildLogEntry,
} from '../services/firebase';
import { AppLayout } from '../components/layout/AppLayout';
import { Badge } from '../components';

// Modal Imports
import { EditEntryModal } from '../components/EditEntryModal';
import { EditProjectModal } from '../components/EditProjectModal';

export function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>();

  // State
  const [project, setProject] = useState<Project | null>(null);
  const [entries, setEntries] = useState<BuildLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal Toggles
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BuildLogEntry | null>(null);

  useEffect(() => {
    if (!projectId) { 
      setError('No project ID provided'); 
      setLoading(false); 
      return; 
    }
    loadData();
  }, [projectId]);

  useEffect(() => {
    if (project) {
      document.title = `Buildlog — ${project.name}`;
    }
  }, [project]);

  const loadData = async () => {
    if (!projectId) return;
    const user = getCurrentUser();
    if (!user) { 
      setError('Not authenticated'); 
      setLoading(false); 
      return; 
    }

    try {
      const [proj, ents] = await Promise.all([
        getProject(projectId),
        getLogEntries(user.uid, { limit: 100 }), 
      ]);
      
      if (!proj) {
        setError('Project not found');
        return;
      }

      setProject(proj);
      // Filter entries strictly belonging to this project
      setEntries(ents.filter(e => e.projectId === projectId));
    } catch (err) {
      setError('Failed to load project data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 1. Loading State
  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center py-40">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#000000] border-t-transparent" />
      </div>
    </AppLayout>
  );

  // 2. Error or Null State (Narrowing for TS)
  if (!project) return (
    <AppLayout>
      <div className="text-center py-40">
        <p className="text-red-500 mb-4 font-display font-medium">{error || 'Project not found'}</p>
        <Link to="/dashboard" className="text-[#000000] font-bold hover:underline">
          Back to Dashboard
        </Link>
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* Navigation & Header */}
        <div>
          <Link to="/dashboard" className="inline-flex items-center text-sm text-[#6C6864] hover:text-[#000000] mb-6 transition-colors group">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </Link>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-100">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <h1 className="text-4xl font-bold text-[#000000] font-display tracking-tight">
                  {project.name}
                </h1>
                
                {/* Edit Project Icon */}
                <button 
                  onClick={() => setIsEditingProject(true)}
                  className="p-1.5 text-slate-400 hover:text-[#000000] hover:bg-slate-50 rounded-md transition-all"
                  title="Edit project details"
                >
                  <Edit2 size={18} />
                </button>

                <Badge status={project.status} />
              </div>
              <p className="text-[#6C6864] max-w-2xl text-lg leading-relaxed">
                {project.description}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button className="px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-[#333333] font-medium hover:border-[#000000] hover:text-[#000000] transition-all flex items-center gap-2 shadow-sm">
                <Upload size={18} />
                <span>Import Archive</span>
              </button>
              <button
                onClick={() => setShowNewEntry(true)}
                className="px-5 py-2.5 rounded-lg bg-[#000000] text-white font-bold hover:bg-[#333333] transition-all flex items-center gap-2 shadow-md active:scale-95"
              >
                <Plus size={20} />
                New Entry
              </button>
            </div>
          </div>
        </div>

        {/* Timeline View */}
        <div className="grid grid-cols-1 gap-4">
          {entries.length === 0 ? (
            <div className="text-center py-24 bg-white border border-slate-200 rounded-2xl">
              <FileText size={48} className="mx-auto text-slate-200 mb-4" />
              <h3 className="text-xl font-bold text-[#000000] font-display">No logs found</h3>
              <p className="text-[#6C6864] mt-1 mb-8">Your build logs will appear here once synced with Claude.</p>
              <button onClick={() => setShowNewEntry(true)} className="text-[#000000] font-bold hover:underline">
                Create a manual entry
              </button>
            </div>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.id}
                className="group flex items-center justify-between bg-white p-6 rounded-2xl border border-slate-200 hover:border-[#000000]/40 hover:shadow-lg transition-all"
              >
                {/* Card Main Link */}
                <Link to={`/projects/${projectId}/entry/${entry.id}`} className="flex gap-6 items-center flex-1">
                  <div className="hidden sm:flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-slate-50 border border-slate-100 text-[#6C6864] group-hover:bg-[#000000]/5 group-hover:text-[#000000] transition-colors">
                    <span className="text-[10px] uppercase font-bold tracking-tighter">
                      {format(entry.timestamp.toDate(), 'MMM')}
                    </span>
                    <span className="text-xl font-bold font-display leading-none">
                      {format(entry.timestamp.toDate(), 'd')}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-[#333333] group-hover:text-[#000000] transition-colors font-display">
                      {entry.title}
                    </h3>
                    <p className="text-[#6C6864] line-clamp-1 font-mono text-sm opacity-70">
                      {entry.content.replace(/[#*`]/g, '')}
                    </p>
                  </div>
                </Link>
                
                {/* Edit Entry Action */}
                <div className="flex items-center gap-2 pl-4">
                  <button 
                    onClick={() => setEditingEntry(entry)}
                    className="p-2 text-slate-400 hover:text-[#000000] hover:bg-slate-50 rounded-lg transition-all"
                    title="Edit Entry"
                  >
                    <Edit2 size={18} />
                  </button>
                  <Link to={`/projects/${projectId}/entry/${entry.id}`} className="p-2 text-slate-200 hover:text-[#000000] transition-colors">
                    <ChevronRight size={20} />
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* --- Modals Layer --- */}
      
      {showNewEntry && (
        <NewEntryModal
          projectId={projectId!}
          onClose={() => setShowNewEntry(false)}
          onSaved={() => { setShowNewEntry(false); loadData(); }}
        />
      )}

      {editingEntry && (
        <EditEntryModal 
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => { setEditingEntry(null); loadData(); }}
        />
      )}

      {isEditingProject && (
        <EditProjectModal 
          project={project}
          onClose={() => setIsEditingProject(false)}
          onSaved={() => { setIsEditingProject(false); loadData(); }}
        />
      )}

    </AppLayout>
  );
}

// ─── Internal Components ───────────────────────────────────────────────────

function NewEntryModal({ projectId, onClose, onSaved }: { projectId: string; onClose: () => void; onSaved: () => void; }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [problemSolved, setProblemSolved] = useState('');
  const [outcome, setOutcome] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const user = getCurrentUser();
      if (!user) return;
      await createLogEntry({
        projectId,
        userId: user.uid,
        title: title.trim(),
        content: content.trim(),
        problemSolved: problemSolved.trim(),
        outcome: outcome.trim(),
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        timestamp: Timestamp.now(),
        sourceType: 'manual',
        published: false,
      });
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#000000]/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-in zoom-in duration-200">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-[#000000] font-display">New Entry</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-[#000000] transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6C6864] mb-1">Title</label>
            <input
              autoFocus
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#000000]/5 focus:border-[#000000] outline-none"
              placeholder="What did you build today?"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6C6864] mb-1">Problem Solved</label>
              <input
                value={problemSolved}
                onChange={e => setProblemSolved(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                placeholder="The blocker..."
              />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6C6864] mb-1">Outcome</label>
              <input
                value={outcome}
                onChange={e => setOutcome(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                placeholder="The result..."
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6C6864] mb-1">Content (Markdown)</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              className="w-full h-32 px-4 py-3 border border-slate-200 rounded-lg font-mono text-sm focus:border-[#000000] outline-none resize-none"
              placeholder="# Today's progress..."
            />
          </div>
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[#6C6864] mb-1">Tags (Comma separated)</label>
            <input
              type="text"
              value={tags}
              onChange={e => setTags(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              placeholder="frontend, api, bugfix"
            />
          </div>
        </div>
        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-[#6C6864] font-bold">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || saving}
            className="px-6 py-2 bg-[#000000] text-white rounded-lg font-bold hover:bg-[#333333] transition-all disabled:opacity-50 shadow-sm"
          >
            {saving ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}